import { useCallback, useEffect, useState } from 'react';
import type { N2kDecodeInfo, N2kSupportManifest, N2kTransport } from '@neomaris/protocol';

import { api } from '../api';

export type WriteState = 'read_only' | 'planned' | 'blocked' | 'live_enabled';
export type OperationState =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'stopped'
  | 'refused'
  | 'disarmed';

export interface AnalyzerEvidence {
  id: string;
  source: string;
  observedAt: string;
  confidence: string;
  stale: boolean;
  fields: string[];
  importId?: string;
}

export interface AnalyzerGateway {
  id: string;
  name: string;
  type: string;
  state: string;
  writable: boolean;
  softwareVersion: string | null;
  capabilities: string[];
  evidence: AnalyzerEvidence[];
}

export interface AnalyzerDevice {
  id: string;
  protocol: string;
  stableIdentity: { kind: string; value: string } | null;
  sourceAddresses: Array<{ gatewayId: string; address: number | null; lastSeenAt: string }>;
  manufacturer: { code: number | null; name: string | null } | null;
  deviceClass: { code: number; name: string | null } | null;
  deviceFunction: { code: number; name: string | null } | null;
  product: {
    code: number | null;
    modelId: string | null;
    softwareVersion: string | null;
    modelVersion: string | null;
    serialCode: string | null;
  } | null;
  configuration: {
    installationDescription1: string | null;
    installationDescription2: string | null;
    manufacturerInformation: string | null;
  } | null;
  labels: { device: string | null; channels: Record<string, string> };
  pgns: { transmit: number[] | null; receive: number[] | null };
  firstSeenAt: string;
  lastSeenAt: string;
  messageCount: number;
  evidence: AnalyzerEvidence[];
}

export interface WriteCapability {
  family: string;
  mechanism: string;
  state: WriteState;
  reason: string | null;
  graduation: {
    exchangeCaptured: boolean;
    codecImplemented: boolean;
    productionPathVerified: boolean;
    hardwareVerified: boolean;
    verification: string;
    audit: boolean;
    localArming: boolean;
  };
}

export interface ConfigParameter {
  key: string;
  label: string;
  description?: string;
  value: {
    type: 'string' | 'number' | 'enum' | 'boolean';
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    options?: Array<{ value: number | string; label: string }>;
    maxLength?: number;
    nullable?: boolean;
  };
  /** Absent for a read-only parameter — it describes what a write does. */
  danger?: 'safe' | 'caution' | 'dangerous' | 'blocked';
  confidence: string;
  writeCapability?: WriteCapability;
}

export interface AnalyzerConfigSchema {
  deviceId: string;
  displayName: string;
  definitionIds: string[];
  parameters: ConfigParameter[];
  values: Record<string, unknown>;
}

export interface TrafficRow {
  seq: number;
  gatewayId: string;
  deviceId: string | null;
  timestamp: string;
  pgn: number;
  sourceAddress: number;
  destinationAddress: number;
  priority: number;
  length: number;
  payloadHex: string;
  transport?: N2kTransport;
  decodeInfo?: N2kDecodeInfo;
  decoded?: Record<string, unknown>;
}

export interface AnalyzerDiagnostic {
  id: string;
  code: string;
  severity: 'info' | 'warning' | 'critical' | 'unavailable';
  title: string;
  detail: string;
  recommendation: string;
  affectedDeviceIds: string[];
  evidence: AnalyzerEvidence[];
}

export interface ToolkitImport {
  id: string;
  vendor: 'maretron' | 'actisense';
  filename: string;
  status: 'complete' | 'partial';
  confidence: string;
  importedAt: string;
  observedAt: string | null;
  stale: boolean;
  warnings: string[];
}

export interface SnapshotSummary {
  id: string;
  kind: 'live' | 'backup' | 'pre_write' | 'import';
  reason: string | null;
  createdAt: string;
}

export interface AnalyzerSnapshot {
  v: 1;
  id: string;
  boatId: string;
  kind: 'live' | 'backup' | 'pre_write' | 'import';
  createdAt: string;
  gateways: AnalyzerGateway[];
  devices: AnalyzerDevice[];
  traffic: {
    bufferCapacity: number;
    bufferedRows: number;
    droppedRows: number;
    windowSeconds: number;
    recent: TrafficRow[];
    rates: Array<{
      gatewayId: string;
      deviceId: string | null;
      pgn: number;
      sourceAddress: number;
      framesPerSecond: number;
      frameCount: number;
      windowSeconds: number;
    }>;
  };
  diagnostics: AnalyzerDiagnostic[];
  imports: ToolkitImport[];
  savedSnapshots: SnapshotSummary[];
  capabilityMatrix: Array<
    WriteCapability & {
      definitionId: string;
      parameterKey: string;
      parameterLabel: string;
      danger?: string;
      confidence: string;
    }
  >;
  configSchemas: AnalyzerConfigSchema[];
  n2kSupport?: N2kSupportManifest;
}

export interface ToolkitOperation {
  v: 1;
  id: string;
  boatId: string;
  type: 'snapshot' | 'import' | 'capture' | 'plan' | 'apply';
  state: OperationState;
  idempotencyKey: string;
  target: Record<string, unknown>;
  progress: {
    completed: number;
    total: number;
    unit: string;
    message: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string;
  resultRef: string | null;
  failure: { code: string; message: string; retryable: boolean } | null;
  auditIds: string[];
}

export interface ConfigPlan {
  v: 1;
  id: string;
  operationId: string;
  boatId: string;
  deviceId: string;
  targetStableIdentity: { kind: 'n2k_name'; value: string } | null;
  snapshotId: string | null;
  gatewayIds: string[];
  createdAt: string;
  expiresAt: string;
  steps: Array<{
    index: number;
    parameterKey: string;
    label: string;
    before: unknown;
    after: unknown;
    danger?: string;
    capabilityState: WriteState;
    status: string;
    result?: Record<string, unknown>;
  }>;
  preflight: Array<{
    code: string;
    severity: 'blocking' | 'warning';
    message: string;
    parameterKey?: string;
  }>;
  canApply: boolean;
}

export interface ToolkitAuditEntry {
  id: string;
  operationId: string | null;
  deviceName: string | null;
  parameterKey: string | null;
  event: string;
  disposition: string | null;
  detail: string | null;
  createdAt: string;
}

type SnapshotState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; snapshot: AnalyzerSnapshot };

export function useMarineToolkitSnapshot(boatId: string) {
  const [state, setState] = useState<SnapshotState>({ kind: 'loading' });

  const refresh = useCallback(async () => {
    if (!boatId) return;
    try {
      const snapshot = await api<AnalyzerSnapshot>(`/v1/boats/${boatId}/toolkit/snapshot`);
      setState({ kind: 'ready', snapshot });
    } catch (error) {
      if (apiErrorCode(error) === 'toolkit_unavailable') {
        setState({ kind: 'unavailable' });
      } else {
        setState({ kind: 'error', message: apiErrorMessage(error) });
      }
    }
  }, [boatId]);

  useEffect(() => {
    let active = true;
    const run = () => {
      if (active) void refresh();
    };
    run();
    const timer = window.setInterval(() => {
      if (!document.hidden) run();
    }, 3_000);
    const onVisibility = () => {
      if (!document.hidden) run();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  return { state, refresh };
}

export function toolkitKey(action: string): string {
  const suffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `toolkit-${action}-${suffix}`;
}

export async function refreshToolkitSnapshot(boatId: string): Promise<ToolkitOperation> {
  return mutation(boatId, '/snapshot/refresh', 'snapshot-refresh');
}

export async function saveToolkitSnapshot(
  boatId: string,
  reason?: string
): Promise<ToolkitOperation> {
  return mutation(boatId, '/snapshots', 'snapshot-save', reason ? { reason } : {});
}

export async function importToolkitEvidence(
  boatId: string,
  input: { filename: string; vendor?: 'maretron' | 'actisense'; contentBase64: string }
): Promise<ToolkitOperation> {
  return mutation(boatId, '/imports', 'import', input);
}

export async function startToolkitCapture(
  boatId: string,
  input: {
    maxDurationSeconds: number;
    maxBytes: number;
    filters?: Record<string, string | number>;
  }
): Promise<ToolkitOperation> {
  return mutation(boatId, '/captures', 'capture-start', input);
}

export async function stopToolkitCapture(
  boatId: string,
  operationId: string
): Promise<ToolkitOperation> {
  return mutation(boatId, `/captures/${encodeURIComponent(operationId)}/stop`, 'capture-stop');
}

export async function createToolkitPlan(
  boatId: string,
  draft: {
    v: 1;
    deviceId: string;
    snapshotId?: string;
    changes: Array<{ paramKey: string; value: unknown }>;
  }
): Promise<ToolkitOperation> {
  return mutation(boatId, '/config/plans', 'plan-create', draft);
}

export async function applyToolkitPlan(boatId: string, planId: string): Promise<ToolkitOperation> {
  return mutation(boatId, `/config/plans/${encodeURIComponent(planId)}/apply`, 'plan-apply');
}

export async function getToolkitOperation(
  boatId: string,
  operationId: string
): Promise<ToolkitOperation> {
  return api(`/v1/boats/${boatId}/toolkit/operations/${encodeURIComponent(operationId)}`);
}

export async function getToolkitResult<T>(boatId: string, operationId: string): Promise<T> {
  const response = await api<{ result: T }>(
    `/v1/boats/${boatId}/toolkit/operations/${encodeURIComponent(operationId)}/result`
  );
  return response.result;
}

export async function waitForToolkitOperation(
  boatId: string,
  operationId: string,
  options: { timeoutMs?: number; onUpdate?: (operation: ToolkitOperation) => void } = {}
): Promise<ToolkitOperation> {
  const deadline = Date.now() + (options.timeoutMs ?? 120_000);
  while (Date.now() < deadline) {
    const operation = await getToolkitOperation(boatId, operationId);
    options.onUpdate?.(operation);
    if (isTerminalOperation(operation)) return operation;
    await delay(750);
  }
  throw new Error('Operation status timed out');
}

export async function getToolkitTraffic(
  boatId: string,
  filters: Record<string, string | number | undefined>
): Promise<{
  rows: TrafficRow[];
  rates: AnalyzerSnapshot['traffic']['rates'];
  page: { limit: number; nextCursor: string | null; hasMore: boolean };
  droppedRows: number;
}> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  return api(`/v1/boats/${boatId}/toolkit/traffic?${query.toString()}`);
}

export async function getN2kSupport(boatId: string): Promise<N2kSupportManifest> {
  return api(`/v1/boats/${boatId}/toolkit/n2k/support`);
}

export async function listToolkitSnapshots(boatId: string): Promise<SnapshotSummary[]> {
  const response = await api<{ snapshots: SnapshotSummary[] }>(
    `/v1/boats/${boatId}/toolkit/snapshots`
  );
  return response.snapshots;
}

export async function getToolkitSnapshotExport(
  boatId: string,
  snapshotId: string
): Promise<AnalyzerSnapshot> {
  return api(`/v1/boats/${boatId}/toolkit/snapshots/${encodeURIComponent(snapshotId)}/export`);
}

export async function listToolkitAudit(
  boatId: string,
  filters: { operationId?: string; deviceName?: string; limit?: number } = {}
): Promise<ToolkitAuditEntry[]> {
  const query = new URLSearchParams();
  if (filters.operationId) query.set('operationId', filters.operationId);
  if (filters.deviceName) query.set('deviceName', filters.deviceName);
  if (filters.limit) query.set('limit', String(filters.limit));
  const response = await api<{ entries: ToolkitAuditEntry[] }>(
    `/v1/boats/${boatId}/toolkit/audit?${query.toString()}`
  );
  return response.entries;
}

export async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 32_768;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk));
  }
  return btoa(binary);
}

export function isTerminalOperation(operation: ToolkitOperation): boolean {
  return ['succeeded', 'failed', 'stopped', 'refused', 'disarmed'].includes(operation.state);
}

export function apiErrorCode(error: unknown): string | null {
  const body = (error as { body?: unknown })?.body;
  if (!body || typeof body !== 'object') return null;
  const code = (body as Record<string, unknown>).error;
  return typeof code === 'string' ? code : null;
}

export function apiErrorMessage(error: unknown): string {
  const body = (error as { body?: unknown })?.body;
  if (body && typeof body === 'object') {
    const message = (body as Record<string, unknown>).message;
    if (typeof message === 'string') return message;
  }
  return error instanceof Error ? error.message : 'Request failed';
}

async function mutation(
  boatId: string,
  suffix: string,
  action: string,
  body?: unknown
): Promise<ToolkitOperation> {
  const response = await api<{ operation: ToolkitOperation }>(
    `/v1/boats/${boatId}/toolkit${suffix}`,
    {
      method: 'POST',
      ...(body === undefined ? {} : { body }),
      idempotencyKey: toolkitKey(action),
    }
  );
  return response.operation;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
